import { useEffect, useId, useState, type FormEvent } from 'react';
import { ApiRequestError, login, register, type AuthenticatedUser } from './authApi';

export type AuthMode = 'login' | 'register';

type AuthDialogProps = {
  open: boolean;
  initialMode: AuthMode;
  onClose: () => void;
  onAuthenticated: (user: AuthenticatedUser) => void;
};

export function AuthDialog({ open, initialMode, onClose, onAuthenticated }: AuthDialogProps) {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    if (!open) {
      return;
    }
    setMode(initialMode);
    setPassword('');
    setFormError('');
    setFieldErrors({});

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [initialMode, onClose, open]);

  if (!open) {
    return null;
  }

  const changeMode = (nextMode: AuthMode) => {
    setMode(nextMode);
    setFormError('');
    setFieldErrors({});
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setFormError('');
    setFieldErrors({});
    try {
      const user = await (mode === 'register' ? register(email, password) : login(email, password));
      onAuthenticated(user);
      onClose();
    } catch (error) {
      if (error instanceof ApiRequestError) {
        setFormError(error.message);
        setFieldErrors(error.fields ?? {});
      } else {
        setFormError('Sunucuya ulaşılamadı. Bağlantını kontrol edip tekrar dene.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section
        className="auth-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <button className="dialog-close" type="button" onClick={onClose} aria-label="Pencereyi kapat">
          ×
        </button>
        <p className="eyebrow">Oyunla Ödevlendir</p>
        <h2 id={titleId}>{mode === 'register' ? 'Ücretsiz hesap oluştur' : 'Hesabına giriş yap'}</h2>
        <p id={descriptionId} className="dialog-description">
          {mode === 'register'
            ? 'Eğitsel oyunlarını hazırlamak ve sonuçları takip etmek için hesabını oluştur.'
            : 'Oyunlarına ve öğrenci sonuçlarına kaldığın yerden devam et.'}
        </p>

        <div className="auth-tabs" role="tablist" aria-label="Üyelik işlemi">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'login'}
            className={mode === 'login' ? 'active' : ''}
            onClick={() => changeMode('login')}
          >
            Giriş yap
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'register'}
            className={mode === 'register' ? 'active' : ''}
            onClick={() => changeMode('register')}
          >
            Hesap oluştur
          </button>
        </div>

        <form className="auth-form" onSubmit={submit} noValidate>
          <label>
            E-posta adresi
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-invalid={Boolean(fieldErrors.email)}
              required
              autoFocus
            />
            {fieldErrors.email && <small className="field-error">{fieldErrors.email}</small>}
          </label>
          <label>
            Parola
            <input
              type="password"
              autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              aria-invalid={Boolean(fieldErrors.password)}
              minLength={10}
              required
            />
            {mode === 'register' && !fieldErrors.password && (
              <small className="field-hint">En az 10 karakter kullan.</small>
            )}
            {fieldErrors.password && <small className="field-error">{fieldErrors.password}</small>}
          </label>
          {formError && <p className="form-error" role="alert">{formError}</p>}
          <button className="primary-button auth-submit" type="submit" disabled={busy}>
            {busy ? 'Lütfen bekle…' : mode === 'register' ? 'Hesabımı oluştur' : 'Giriş yap'}
          </button>
        </form>
        <p className="privacy-copy">
          Oturum güvenliği için yalnızca zorunlu bir oturum çerezi kullanılır. Hesap oluşturmadan önce
          {' '}<a href="/odevlendir/kvkk" target="_blank" rel="noreferrer">KVKK Aydınlatma Metni</a> ve
          {' '}<a href="/odevlendir/kullanim-kosullari" target="_blank" rel="noreferrer">Kullanım Koşulları</a> incelenebilir.
        </p>
      </section>
    </div>
  );
}
